import { Modal, Select } from 'antd';
import { TFunction } from 'i18next';
import React, { FC, useRef, useState } from 'react';
import { container } from 'src/inversify.config';
import {
  CsvKeys,
  OrganizationDatabaseLogsResultStore,
  OrganizationDatabaseStatusItem,
  OrganizationItemStore,
  getTranslatedHeader,
} from 'src/stores/organizations-store';

import { OrganizationDbLogsExportOption } from 'src/stores/organizations-store';

interface ExportDBLogsModalProps {
  t: TFunction;
  onClose: () => void;
  onExport: () => void;
  logId: string;
  items?: OrganizationDatabaseStatusItem[];
  keys?: {
    key: CsvKeys;
    translatedHeader: string;
  }[];
}

const ExportDBLogsModal: FC<ExportDBLogsModalProps> = ({ onClose, onExport, t, logId, items, keys }) => {
  const logResultStore = container.get(OrganizationDatabaseLogsResultStore);
  const logResultState = logResultStore.state;

  const organizationItemStore = container.get(OrganizationItemStore);
  const [selectedOption, setSelectedOption] = useState<OrganizationDbLogsExportOption>(OrganizationDbLogsExportOption.CSV);
  const exportItems = useRef(items || []);
  const exportKeys = useRef(keys || []);

  const exportLogs = async () => {
    await logResultStore.loadById(logId, {
      searchQuery: '',
      skip: 0,
      take: 100000,
    });

    exportItems.current = logResultState.value.items.map((value) => {
      return {
        organizationName: value.organizationName,
        result: value.success ? t('Success') : `Error: ${value.errorMessage}`,
      } as OrganizationDatabaseStatusItem;
    });

    exportKeys.current = [
      { key: CsvKeys.OrganizationName, translatedHeader: getTranslatedHeader(CsvKeys.OrganizationName, t) },
      { key: CsvKeys.Status, translatedHeader: getTranslatedHeader(CsvKeys.Status, t) },
    ];

    const logData = await logResultStore.getLogData(logId);
    const userName = logData?.userName;
    const createdOn = logData?.createdOn;
    const isSuccess = logData?.success;

    if (userName && createdOn && isSuccess) {
      switch (selectedOption) {
        case OrganizationDbLogsExportOption.CSV:
          await organizationItemStore.getCheckOrganizationsReportCSV(exportItems.current, exportKeys.current);
          break;
        case OrganizationDbLogsExportOption.PDF:
          await organizationItemStore.getCheckOrganizationsReportPDF(exportItems.current, userName, createdOn, isSuccess);
          break;
      }
    }
  };

  return (
    <Modal
      style={{ top: '20%' }}
      title={<>{t('ExportOrganizationDBLogsModalTitle')}</>}
      cancelButtonProps={{ type: 'default', style: { width: '25%' } }}
      open
      onCancel={onClose}
      onOk={async () => {
        await exportLogs();
        onExport();
      }}
    >
      <>{t('Format')}</>
      <Select
        onChange={(value: OrganizationDbLogsExportOption) => setSelectedOption(value)}
        defaultValue={OrganizationDbLogsExportOption.CSV}
        style={{ width: '100%' }}
      >
        {Object.keys(OrganizationDbLogsExportOption).map((option) => (
          <Select.Option key={option} value={option}>
            <>{t(option)}</>
          </Select.Option>
        ))}
      </Select>
    </Modal>
  );
};

export default ExportDBLogsModal;
